import axios, { type InternalAxiosRequestConfig } from "axios";

/** One recorded API call, kept so a crash can be reproduced. */
export interface TrailAction {
  id: string;
  type: "api";
  method: string;
  url: string;
  status: number | null;
  payload: unknown;
  at: string;
}

const STORAGE_KEY = "tripbox:actionTrail";
const MAX_ACTIONS = 5;
const MAX_PAYLOAD_LENGTH = 2000;
const SENSITIVE_KEY_PATTERN = /password|passwd|^pass$|secret|token|authorization|cookie|^otp|otp$|mfa|api[-_]?key|credential|^code$/i;
const SKIPPED_URL_PATTERN = /\/api\/error-logs/;
const SENSITIVE_URL_PATTERN = /\/api\/auth\//;

type TrackedConfig = InternalAxiosRequestConfig & { __trailId?: string };

let installed = false;

const readTrail = (): TrailAction[] => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeTrail = (actions: TrailAction[]) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(actions.slice(-MAX_ACTIONS)));
  } catch {
    // Storage can be full or blocked; losing the trail must never break the app.
  }
};

const redact = (value: unknown, depth = 0): unknown => {
  if (depth > 5) return "[truncated]";
  if (Array.isArray(value)) return value.slice(0, 30).map((item) => redact(item, depth + 1));
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .slice(0, 60)
        .map(([key, item]) => [key, SENSITIVE_KEY_PATTERN.test(key) ? "[redacted]" : redact(item, depth + 1)]),
    );
  }
  return value;
};

/** Redacts credential-like keys and caps the size of a request body or params object. */
const summarizePayload = (url: string, data: unknown, params: unknown): unknown => {
  if (SENSITIVE_URL_PATTERN.test(url)) return "[redacted]";
  let body: unknown = data;
  if (typeof data === "string") {
    try {
      body = JSON.parse(data);
    } catch {
      body = data.slice(0, MAX_PAYLOAD_LENGTH);
    }
  } else if (typeof FormData !== "undefined" && data instanceof FormData) {
    body = "[form-data]";
  } else if (data && typeof data === "object" && !Array.isArray(data) && Object.getPrototypeOf(data) !== Object.prototype) {
    body = "[binary]";
  }

  const summary = { ...(body !== undefined && body !== null ? { body: redact(body) } : {}), ...(params ? { params: redact(params) } : {}) };
  if (Object.keys(summary).length === 0) return null;
  const serialized = JSON.stringify(summary);
  return serialized.length > MAX_PAYLOAD_LENGTH ? `${serialized.slice(0, MAX_PAYLOAD_LENGTH)}…[truncated]` : summary;
};

const redactUrl = (url: string): string =>
  url.replace(/([?&])([^=&]+)=([^&]*)/g, (match, prefix, key) => (SENSITIVE_KEY_PATTERN.test(key) ? `${prefix}${key}=[redacted]` : match));

/** Returns the stored trail, oldest first, for attaching to an error report. */
export const getActionTrail = (): TrailAction[] => readTrail();

/** Clears the trail, e.g. on logout so the next user does not inherit it. */
export const clearActionTrail = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore blocked storage.
  }
};

const updateStatus = (config: TrackedConfig | undefined, status: number | null) => {
  if (!config?.__trailId) return;
  const actions = readTrail();
  const entry = actions.find((action) => action.id === config.__trailId);
  if (!entry) return;
  entry.status = status;
  writeTrail(actions);
};

/**
 * Starts recording the last five API calls (method, url, redacted payload, status) in
 * sessionStorage. Idempotent; call once at startup, before any request is made.
 */
export const installActionTrail = () => {
  if (installed) return;
  installed = true;

  axios.interceptors.request.use((config: TrackedConfig) => {
    const url = config.url || "";
    if (SKIPPED_URL_PATTERN.test(url) || config.__trailId) return config;
    try {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      config.__trailId = id;
      writeTrail([
        ...readTrail(),
        {
          id,
          type: "api",
          method: (config.method || "get").toUpperCase(),
          url: redactUrl(url),
          status: null,
          payload: summarizePayload(url, config.data, config.params),
          at: new Date().toISOString(),
        },
      ]);
    } catch {
      // Recording is best effort.
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => {
      updateStatus(response.config as TrackedConfig, response.status);
      return response;
    },
    (error) => {
      updateStatus(error?.config as TrackedConfig | undefined, error?.response?.status ?? 0);
      return Promise.reject(error);
    },
  );
};
